import { createContext, useEffect, useRef, useState } from 'react'
import { NextPage } from 'next'

import classnames from 'classnames'
import styles from '../_playlistsPage.module.scss'

import { staticProps } from '../../../utils/createApolloClient'
import ThemeComponent from '../components/ThemeComponent'
import ListSort from '../../../components/ui/ListSort'
import SplashScreen from '../../../components/SplashScreen'
import { useLazyQuery } from '@apollo/client'
import { THEMES_LIST_QUERY } from '../../../gql'
import SortByLetter from '../../../components/ui/SortByLetter'
import PlaylistsLayout from '../components/PlaylistsLayout'
import { SortSongsEnum, Theme } from '../../../__generated__/graphql'

const SearchContext = createContext(null)

interface FiltersState {
  orderBy: SortSongsEnum
  favorite: boolean
}

const initalFiltersState: FiltersState = {
  favorite: false,
  orderBy: SortSongsEnum.AToZ
}

let currentLetter = ''

const returnColor = (size, color, i, diff) => {
  const colorStart = Math.round(i / Math.round(size / (diff.length - 1)))
  const colorStop = colorStart + 1

  return color[colorStart] === color[colorStop]
    ? color[colorStart]
    : color[colorStart] > color[colorStop]
      ? color[colorStart] - i * diff[colorStart]
      : color[colorStart] + i * diff[colorStart]
}

const themesList = (themes) => {
  return themes.map((theme, index) => {
    const themeFirstLetter = theme.name.toUpperCase().charAt(0)
    let anchor = null
    if (currentLetter !== themeFirstLetter) {
      currentLetter = themeFirstLetter
      anchor = <a id={`letter-${currentLetter}`}></a>
    }

    const [rColor, gColor, bColor] = [...gradients(themes)[index]]

    return (
      <div
        key={`${index}-${theme.name}`}
        className={classnames(
          styles.themeItem,
          'column is-half-mobile is-one-third-tablet is-one-third-desktop is-one-quarter-widescreen'
        )}
      >
        {anchor}
        <ThemeComponent
          theme={theme}
          color={`rgb(${rColor} ${gColor} ${bColor})`}
        />
      </div>
    )
  })
}

const gradientOptions = {
  r: [220, 0, 40],
  g: [10, 190],
  b: [130, 165]
}

const returnDiff = (colors, size) => {
  return colors
    .map(
      (_, i) =>
        Math.abs(colors[i] - colors[i + 1]) / (size / (colors.length - 1))
    )
    .splice(0, colors.length - 1)
}

const getGradients = (gradient) => {
  const size = gradient.size
  const rDiff = returnDiff(gradient.r, size)
  const gDiff = returnDiff(gradient.g, size)
  const bDiff = returnDiff(gradient.b, size)

  return Array(gradient.size)
    .fill(0)
    .map((_, index) => {
      const rColor = returnColor(gradient.size, gradient.r, index, rDiff)
      const gColor = returnColor(gradient.size, gradient.g, index, gDiff)
      const bColor = returnColor(gradient.size, gradient.b, index, bDiff)
      return [rColor, gColor, bColor]
    })
}
const gradients = (themes) =>
  getGradients({ ...gradientOptions, size: themes.length })

const ThemesPlaylistsPage: NextPage<{
  ssrThemes: Theme[]
}> = ({ ssrThemes }) => {
  const [filtersState, setFiltersState] =
    useState<FiltersState>(initalFiltersState)
  const initialLoad = useRef(true)
  const [themes, setThemes] = useState<Theme[]>(ssrThemes)

  const sortLetters = () => {
    if (
      [SortSongsEnum.AToZ, SortSongsEnum.ZToA].includes(filtersState.orderBy)
    ) {
      return (
        <SortByLetter
          reversed={filtersState.orderBy === SortSongsEnum.ZToA}
          items={themes.map((el) => el.name)}
        />
      )
    }
  }

  const [performSearch, { loading }] = useLazyQuery(THEMES_LIST_QUERY, {
    onCompleted: ({ themes }) => {
      setThemes(themes)
    }
  })

  useEffect(() => {
    if (!initialLoad.current) performSearch({ variables: filtersState })
    else initialLoad.current = false
  }, [filtersState])

  if (loading) {
    return <SplashScreen />
  }
  return (
    <PlaylistsLayout>
      <SearchContext.Provider
        value={{
          filtersState: filtersState,
          setFiltersState: setFiltersState
        }}
      >
        <div className={`${styles.allThemes} page-content`}>
          <div className="level">
            <div className="level-left" />
            <div className="level-right">
              <ListSort SearchContext={SearchContext} />
            </div>
          </div>
          {sortLetters()}
          <div
            className={'columns is-variable is-multiline is-mobile is-3-fullhd'}
          >
            {themesList(themes)}
          </div>
        </div>
      </SearchContext.Provider>
    </PlaylistsLayout>
  )
}

export const getStaticProps = staticProps({
  additionalProps: async ({ client }) => {
    const {
      data: { themes: ssrThemes }
    } = await client.query({
      query: THEMES_LIST_QUERY
    })

    return {
      ssrThemes
    }
  }
})

export default ThemesPlaylistsPage
